import type { Subscription } from '../../../../../types';

import { format, sub } from 'date-fns';
import DATE_FORMATTING_STRINGS from '../../../../../constants/date-formatting-strings';
import RichTranslation from '@blueshift-ui/i18n/dist/components/rich-translation';
import formatNumber from '../../../../../helpers/format-number';
import { useMemo } from 'react';
import useTranslation from '@blueshift-ui/i18n/dist/hooks/use-translation';

interface SubscriptionDetailsProps {
  subscription: Subscription;
}

function SubscriptionDetails({
  subscription: {
    contract: { duration_periods, end_date, periods_remaining },
    currency_code,
    discounted_price_cents,
    entitlements,
    is_contract,
    name,
    next_billing_date,
    purchase_price_cents,
    supplemental_rate_cents_per_hour,
  },
}: SubscriptionDetailsProps) {
  const { translate: richTranslate } = useTranslation('update.membership.options', { rich: true });
  const { translate } = useTranslation('update.membership.options');
  const supplementalRate = supplemental_rate_cents_per_hour
    ? formatNumber({
        currency: currency_code,
        number: supplemental_rate_cents_per_hour / 100,
      })
    : '';

  const showSupplementalRate = Boolean(supplementalRate.length);

  const planStringKey = showSupplementalRate
    ? 'plan_details_with_supplemental_rate'
    : 'plan_details';

  const tutoringHours =
    entitlements?.find(({ type }) => type === 'tutoring_hours')?.configuration?.number_of_hours ||
    '';

  const term = useMemo(() => {
    if (is_contract) {
      const beginDate = format(
        sub(new Date(next_billing_date), { months: duration_periods - periods_remaining }),
        DATE_FORMATTING_STRINGS.monthOfYear
      );
      const endDate = format(new Date(end_date), DATE_FORMATTING_STRINGS.monthOfYear);
      return `${beginDate} - ${endDate}`;
    }

    return translate('monthly_term');
  }, [duration_periods, end_date, is_contract, next_billing_date, periods_remaining, translate]);

  return (
    <>
      <RichTranslation
        i18nKey={planStringKey}
        replace={{
          duration: is_contract
            ? `${duration_periods} ${translate('contract_duration')}`
            : translate('monthly_duration'),
          next_billing_date: format(new Date(next_billing_date), DATE_FORMATTING_STRINGS.dayOfYear),
          plan: name,
          price: formatNumber({
            currency: currency_code,
            number: (discounted_price_cents || purchase_price_cents) / 100,
          }),
          supplemental_rate: supplementalRate,
          term: term,
          tutoring_hours: tutoringHours,
        }}
        translator={richTranslate}
      />
    </>
  );
}

export default SubscriptionDetails;
